import { useCallback, useEffect, useMemo, useState } from "react";
import { ChefHat, ChevronLeft, ChevronRight, CircleCheckBig, Clock3, Flame } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import { MobileShell } from "@/components/MobileShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { api, getApiError } from "@/lib/api";


export default function CookModePage() {
  const navigate = useNavigate();
  const { itemId } = useParams();
  const [cookMode, setCookMode] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [checkedIngredients, setCheckedIngredients] = useState({});
  const [form, setForm] = useState({ leftover_portions: "0", best_before: "", notes: "" });

  const loadCookMode = useCallback(async () => {
    const response = await api.get(`/cook/${itemId}`);
    setCookMode(response.data);
    setForm({
      leftover_portions: String(response.data.suggested_leftover_portions || 0),
      best_before: response.data.default_best_before || "",
      notes: "",
    });
  }, [itemId]);

  useEffect(() => {
    loadCookMode();
  }, [loadCookMode]);

  const activeStep = useMemo(
    () => cookMode?.recipe?.instructions?.[currentStep],
    [cookMode, currentStep],
  );

  const completeCook = async () => {
    try {
      const response = await api.post(`/cook/${itemId}/complete`, {
        leftover_portions: Number(form.leftover_portions),
        best_before: form.best_before || null,
        notes: form.notes || null,
      });
      const pantryCount = response.data.pantry_update_count || 0;
      toast.success(
        pantryCount
          ? `Meal gelogd. Pantry bijgewerkt voor ${pantryCount} ingrediënten.`
          : "Meal gelogd en leftovers bijgewerkt.",
      );
      navigate("/leftovers");
    } catch (error) {
      toast.error(getApiError(error));
    }
  };

  return (
    <MobileShell title="Cook mode" eyebrow="Stap voor stap koken" action={<Button variant="outline" className="rounded-full border-stone-200 bg-white" onClick={() => navigate("/planner")} data-testid="cook-mode-back-planner-button">Planner</Button>}>
      <div className="space-y-4" data-testid="cook-mode-page">
        <section className="rounded-[28px] border border-stone-200 bg-[linear-gradient(135deg,_rgba(243,230,198,0.9),_rgba(236,245,234,0.95))] p-4" data-testid="cook-mode-summary-card">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-stone-500" data-testid="cook-mode-kicker">
            Vandaag koken
          </p>
          <h2 className="mt-2 text-2xl font-semibold text-stone-900" data-testid="cook-mode-title">
            {cookMode?.recipe?.title || "Cook mode laden..."}
          </h2>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-orange-100 px-3 py-1 text-orange-700" data-testid="cook-mode-kcal-pill">
              <Flame className="mr-1 inline h-3 w-3" />
              {cookMode?.recipe?.kcal_per_serving || 0} kcal
            </span>
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800" data-testid="cook-mode-carb-pill">
              {cookMode?.recipe?.carbs_per_serving || 0} g carbs
            </span>
            <span className="rounded-full bg-stone-100 px-3 py-1 text-stone-700" data-testid="cook-mode-time-pill">
              <Clock3 className="mr-1 inline h-3 w-3" />
              {(cookMode?.recipe?.prep_time_min || 0) + (cookMode?.recipe?.cook_time_min || 0)} min
            </span>
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800" data-testid="cook-mode-leftover-suggestion-pill">
              Suggestie leftovers: {cookMode?.suggested_leftover_portions || 0}
            </span>
          </div>
        </section>

        <section className="rounded-[28px] border border-stone-200 bg-white/90 p-4" data-testid="cook-mode-ingredients-card">
          <div className="flex items-center gap-3">
            <ChefHat className="h-5 w-5 text-emerald-700" />
            <h3 className="text-lg font-semibold text-stone-900" data-testid="cook-mode-ingredients-title">
              Mise en place
            </h3>
          </div>
          <div className="mt-4 space-y-3">
            {cookMode?.recipe?.ingredients?.map((ingredient) => (
              <button
                key={ingredient.ingredient_id}
                type="button"
                onClick={() => setCheckedIngredients((current) => ({ ...current, [ingredient.ingredient_id]: !current[ingredient.ingredient_id] }))}
                className="flex w-full items-center justify-between rounded-[22px] border border-stone-200 bg-stone-50 px-3 py-3 text-left"
                data-testid={`cook-mode-ingredient-toggle-${ingredient.ingredient_id}`}
              >
                <div>
                  <p className="text-sm font-medium text-stone-900" data-testid={`cook-mode-ingredient-name-${ingredient.ingredient_id}`}>
                    {ingredient.ingredient_name}
                  </p>
                  <p className="mt-1 text-xs text-stone-500" data-testid={`cook-mode-ingredient-qty-${ingredient.ingredient_id}`}>
                    {ingredient.quantity} {ingredient.unit}
                  </p>
                </div>
                {checkedIngredients[ingredient.ingredient_id] ? <CircleCheckBig className="h-5 w-5 text-emerald-700" data-testid={`cook-mode-ingredient-checked-${ingredient.ingredient_id}`} /> : null}
              </button>
            ))}
          </div>
        </section>

        <section className="rounded-[28px] border border-stone-200 bg-white/90 p-4" data-testid="cook-mode-step-card">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-stone-500" data-testid="cook-mode-step-kicker">
              Stap {currentStep + 1} van {cookMode?.recipe?.instructions?.length || 0}
            </p>
            <div className="rounded-full bg-stone-100 px-3 py-1 text-xs text-stone-600" data-testid="cook-mode-step-progress">
              {Math.round(((currentStep + 1) / Math.max(cookMode?.recipe?.instructions?.length || 1, 1)) * 100)}%
            </div>
          </div>
          <p className="mt-4 text-lg font-medium text-stone-900" data-testid="cook-mode-step-text">
            {activeStep}
          </p>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <Button variant="outline" className="h-12 rounded-full border-stone-200 bg-white" onClick={() => setCurrentStep((current) => Math.max(current - 1, 0))} disabled={currentStep === 0} data-testid="cook-mode-prev-step-button">
              <ChevronLeft className="mr-2 h-4 w-4" />
              Vorige
            </Button>
            <Button className="h-12 rounded-full bg-stone-900 text-white hover:bg-stone-800" onClick={() => setCurrentStep((current) => Math.min(current + 1, (cookMode?.recipe?.instructions?.length || 1) - 1))} disabled={currentStep >= (cookMode?.recipe?.instructions?.length || 1) - 1} data-testid="cook-mode-next-step-button">
              Volgende
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </section>

        <section className="rounded-[28px] border border-stone-200 bg-white/90 p-4" data-testid="cook-mode-complete-card">
          <h3 className="text-lg font-semibold text-stone-900" data-testid="cook-mode-complete-title">
            Klaar met koken?
          </h3>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="leftover-portions">
                Leftover porties
              </label>
              <Input id="leftover-portions" type="number" min="0" value={form.leftover_portions} onChange={(event) => setForm((current) => ({ ...current, leftover_portions: event.target.value }))} className="h-12 rounded-2xl border-stone-200" data-testid="cook-mode-leftover-portions-input" />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="leftover-best-before">
                Best before
              </label>
              <Input id="leftover-best-before" type="date" value={form.best_before} onChange={(event) => setForm((current) => ({ ...current, best_before: event.target.value }))} className="h-12 rounded-2xl border-stone-200" data-testid="cook-mode-best-before-input" />
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="cook-notes">
              Notities
            </label>
            <textarea id="cook-notes" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} className="min-h-[96px] w-full rounded-[20px] border border-stone-200 bg-white px-4 py-3 text-sm text-stone-700 outline-none focus:border-emerald-600" data-testid="cook-mode-notes-input" />
          </div>
          <Button className="mt-4 h-12 w-full rounded-full bg-emerald-700 text-white hover:bg-emerald-800" onClick={completeCook} data-testid="cook-mode-complete-button">
            Rond koken af
          </Button>
        </section>
      </div>
    </MobileShell>
  );
}